import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMain } from 'src/views/hooks/useMain';

const ClientBillFilter = ({ open, onClose, onApplyFilter, appliedFilters, clientId, billData }) => {
  const { activeBranchId, getSingleBranch } = useMain();
  const [isLoading, setIsLoading] = useState(false);
  const [servicesByCategory, setServicesByCategory] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeFilter, setActiveFilter] = useState('services');
  const [searchTerm, setSearchTerm] = useState('');
  const [selection, setSelection] = useState({
    services: [],
    products: [],
  });

  useEffect(() => {
    if (!open || !billData) {
      return;
    }
    const processData = () => {
      setIsLoading(true);
      try {
        // Extract unique services from billData and group by category
        const allServicesFromBills = billData.flatMap((bill) =>
          bill.services?.map((s) => s.service).filter(Boolean) || []
        );
        const uniqueServices = [
          ...new Map(
            allServicesFromBills
              .map((s) => ({
                ...s,
                _id: s?._id || s?.serviceId || s?.id || s?.service_id,
                serviceName: s?.serviceName || s?.name || s?.service_name,
                category: s?.category || 'Uncategorized',
              }))
              .filter((s) => s._id && s.serviceName)
              .map((item) => [item.serviceName, item]),
          ).values(),
        ];

        // Group services by category
        const servicesGroupedByCategory = uniqueServices.reduce((acc, service) => {
          const category = service.category || 'Uncategorized';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(service);
          return acc;
        }, {});

        const servicesByCategory = Object.keys(servicesGroupedByCategory).map((category) => ({
          category,
          services: servicesGroupedByCategory[category],
        }));

        // Extract unique products from billData
        const allProductsFromBills = billData.flatMap((bill) =>
          bill.products?.map((p) => p.product).filter(Boolean) || []
        );
        const uniqueProducts = [
          ...new Map(
            allProductsFromBills
              .map((p) => ({
                ...p,
                _id: p?._id || p?.productId || p?.id || p?.product_id,
                productName: p?.productName || p?.consumeableProductName || p?.name || p?.product_name,
              }))
              .filter((p) => p._id && p.productName)
              .map((item) => [item.productName, item]),
          ).values(),
        ];

        setServicesByCategory(servicesByCategory);
        setProducts(uniqueProducts);

        const validServiceIds = uniqueServices.map((s) => s._id);
        const validProductIds = uniqueProducts.map((p) => p._id);

        const currentApplied = appliedFilters || { services: [], products: [] };

        setSelection({
          services: currentApplied.services?.filter((id) => validServiceIds.includes(id)) || [],
          products: currentApplied.products?.filter((id) => validProductIds.includes(id)) || [],
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    processData();
  }, [open, billData, appliedFilters]);

  const handleToggle = (type, id) => {
    setSelection((prevSelection) => {
      const currentSelected = prevSelection[type];
      const currentIndex = currentSelected.indexOf(id);
      const newSelected = [...currentSelected];

      if (currentIndex === -1) {
        newSelected.push(id);
      } else {
        newSelected.splice(currentIndex, 1);
      }

      return {
        ...prevSelection,
        [type]: newSelected,
      };
    });
  };

  const handleToggleCategory = (category) => {
    const categoryServices = servicesByCategory.find((cat) => cat.category === category)?.services || [];
    const serviceIds = categoryServices.map((s) => s._id);
    const allSelected = serviceIds.every((id) => selection.services.includes(id));

    setSelection((prev) => {
      let newServices = [...prev.services];
      if (allSelected) {
        // Deselect all services in category
        newServices = newServices.filter((id) => !serviceIds.includes(id));
      } else {
        // Select all services in category
        const toAdd = serviceIds.filter((id) => !newServices.includes(id));
        newServices = [...newServices, ...toAdd];
      }
      return { ...prev, services: newServices };
    });
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    const currentList = getCurrentList();
    const ids = currentList.map((item) => item._id);

    setSelection((prev) => {
      const currentSelected = prev[activeFilter];
      let newSelected;
      if (checked) {
        const toAdd = ids.filter((id) => !currentSelected.includes(id));
        newSelected = [...currentSelected, ...toAdd];
      } else {
        newSelected = currentSelected.filter((id) => !ids.includes(id));
      }
      return { ...prev, [activeFilter]: newSelected };
    });
  };

  const getCurrentList = () => {
    if (activeFilter === 'services') {
      const allServices = servicesByCategory.flatMap((cat) => cat.services);
      return allServices.filter((item) => {
        const name = item.serviceName;
        return name?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    } else if (activeFilter === 'products') {
      return products.filter((item) => {
        const name = item.productName;
        return name?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    return [];
  };

  const handleApply = () => {
    onApplyFilter(selection);
    onClose();
  };

  const handleClear = () => {
    const clearedSelection = {
      services: [],
      products: [],
    };
    setSelection(clearedSelection);
    onApplyFilter(clearedSelection);
    onClose();
  };

  const handleFilterChange = (newFilter) => {
    setActiveFilter(newFilter);
    setSearchTerm('');
  };

  const currentList = getCurrentList();
  const isAllSelected = currentList.length > 0 && currentList.every((item) => selection[activeFilter].includes(item._id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Filter Bills</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={1} sx={{ minHeight: 350 }}>
            {/* Sidebar */}
            <Grid item xs={3}>
              <List component="nav" sx={{ borderRight: '1px solid #ddd', height: '100%', pt: 0 }}>
                <ListItem button selected={activeFilter === 'services'} onClick={() => handleFilterChange('services')}>
                  <ListItemText primary="Services" primaryTypographyProps={{ fontSize: '16px' }} />
                </ListItem>
                <ListItem button selected={activeFilter === 'products'} onClick={() => handleFilterChange('products')}>
                  <ListItemText primary="Products" primaryTypographyProps={{ fontSize: '16px' }} />
                </ListItem>
              </List>
            </Grid>

            {/* Content */}
            <Grid item xs={9}>
              <Box sx={{ p: 0.5, borderBottom: '1px solid #ddd' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Search ${activeFilter}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ height: 250, overflow: 'auto', p: 0.5 }}>
                {activeFilter === 'services' ? (
                  <>
                    <ListItem
                      button
                      onClick={(e) => handleSelectAll({ target: { checked: !isAllSelected } })}
                    >
                      <ListItemText primary="Select All" primaryTypographyProps={{ fontSize: '15px' }} />
                      <Checkbox
                        edge="end"
                        checked={isAllSelected}
                      />
                    </ListItem>
                    {servicesByCategory.map((cat) => {
                      const categoryServices = cat.services.filter((service) =>
                        service.serviceName?.toLowerCase().includes(searchTerm.toLowerCase())
                      );
                      if (categoryServices.length === 0) return null;
                      const allSelected = cat.services.every((s) => selection.services.includes(s._id));
                      return (
                        <Accordion key={cat.category} defaultExpanded sx={{ '&:before': { display: 'none' }, mb: 0 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5, minHeight: 'auto' }}>
                            <ListItemText primary={cat.category} primaryTypographyProps={{ fontSize: '15px', fontWeight: 'bold' }} />
                            <Checkbox
                              edge="end"
                              checked={allSelected}
                              onChange={() => handleToggleCategory(cat.category)}
                            />
                          </AccordionSummary>
                          <AccordionDetails sx={{ mt: -1, pb: 0, pl: 1, pr: 0.5, p: 0 }}>
                            <List dense sx={{ py: 0 }}>
                              {categoryServices.map((service) => (
                                <ListItem
                                  key={service._id}
                                  secondaryAction={
                                    <Checkbox
                                      edge="end"
                                      checked={selection.services.includes(service._id)}
                                    />
                                  }
                                  disablePadding
                                  button
                                  onClick={() => handleToggle('services', service._id)}
                                  sx={{ mb: 0.25 }}
                                >
                                  <ListItemText primary={service.serviceName} primaryTypographyProps={{ fontSize: '15px' }} />
                                </ListItem>
                              ))}
                            </List>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                    {servicesByCategory.every((cat) => cat.services.filter((s) => s.serviceName?.toLowerCase().includes(searchTerm.toLowerCase())).length === 0) && (
                      <ListItem>
                        <ListItemText primary="No items found" sx={{ textAlign: 'center', color: 'text.secondary' }} />
                      </ListItem>
                    )}
                  </>
                ) : (
                  <List dense>
                    <ListItem
                      button
                      onClick={(e) => handleSelectAll({ target: { checked: !isAllSelected } })}
                    >
                      <ListItemText primary="Select All" primaryTypographyProps={{ fontSize: '15px' }} />
                      <Checkbox
                        edge="end"
                        checked={isAllSelected}
                      />
                    </ListItem>
                    {currentList.map((item) => (
                      <ListItem
                        key={item._id}
                        secondaryAction={
                          <Checkbox
                            edge="end"
                            checked={selection[activeFilter].includes(item._id)}
                          />
                        }
                        disablePadding
                        button
                        onClick={() => handleToggle(activeFilter, item._id)}
                      >
                        <ListItemText primary={item.productName} primaryTypographyProps={{ fontSize: '15px' }} />
                      </ListItem>
                    ))}
                    {currentList.length === 0 && (
                      <ListItem>
                        <ListItemText primary="No items found" sx={{ textAlign: 'center', color: 'text.secondary' }} />
                      </ListItem>
                    )}
                  </List>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear}>Clear</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained" disabled={isLoading}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientBillFilter;